import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, CircleMarker, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { FiMaximize2, FiMapPin } from 'react-icons/fi';
import request from '../../utils/request';
import { fmtNumber } from './derive';
import { countUniqueMapStates, resolveMapPointState } from '../../utils/mapCoverageState';

// Coverage map: lightweight projection from /orders/map (storeLat/storeLng).
// Cluster markers visually by sizing radius with the count of points sharing
// the same ~0.5° lat/lng bin so dense urban areas stand out without flooding.

const MAP_PALETTE = ['#6366f1', '#0ea5e9', '#14b8a6', '#f59e0b', '#f43f5e', '#8b5cf6'];

function FitAllControl({ bounds }) {
  const map = useMap();
  const did = useRef(false);
  const fit = useCallback(() => {
    if (!bounds) return;
    map.fitBounds(bounds, { padding: [32, 32] });
  }, [map, bounds]);
  useEffect(() => {
    if (!did.current && bounds) {
      did.current = true;
      fit();
    }
  }, [bounds, fit]);
  return (
    <button
      type="button"
      onClick={fit}
      className="absolute right-3 top-3 z-[600] inline-flex items-center gap-1 rounded-md border border-zinc-200 bg-white/90 px-2 py-1 text-[11px] font-medium text-zinc-700 shadow-sm backdrop-blur hover:bg-white"
    >
      <FiMaximize2 className="h-3 w-3" />
      Fit all
    </button>
  );
}

export default function DashboardMapCard({ onPointClick }) {
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const { data } = await request.get('/orders/map', {
          params: { limit: 2500, status: 'completed' },
        });
        const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
        if (!cancel) {
          const mapped = list
            .map((d) => ({
              id: String(d._id || ''),
              lat: Number(d.storeLat),
              lng: Number(d.storeLng),
              label: d.storeLocation || d.uniqueId || 'Order',
              storeLocation: d.storeLocation || '',
              state: d.state || d.propertyState || null,
            }))
            .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
          setPoints(mapped);
        }
      } catch (e) {
        if (!cancel) setError(e?.response?.data?.error || 'Failed to load map');
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  const uniqueStates = useMemo(() => countUniqueMapStates(points), [points]);

  const { bounds, binCounts, total } = useMemo(() => {
    const counts = new Map();
    let minLat = Infinity;
    let maxLat = -Infinity;
    let minLng = Infinity;
    let maxLng = -Infinity;
    points.forEach((p) => {
      const key = `${Math.round(p.lat * 2) / 2}|${Math.round(p.lng * 2) / 2}`;
      counts.set(key, (counts.get(key) || 0) + 1);
      if (p.lat < minLat) minLat = p.lat;
      if (p.lat > maxLat) maxLat = p.lat;
      if (p.lng < minLng) minLng = p.lng;
      if (p.lng > maxLng) maxLng = p.lng;
    });
    const b =
      points.length >= 2
        ? L.latLngBounds([minLat, minLng], [maxLat, maxLng])
        : null;
    return { bounds: b, binCounts: counts, total: points.length };
  }, [points]);

  const radiusFor = useCallback(
    (p) => {
      const key = `${Math.round(p.lat * 2) / 2}|${Math.round(p.lng * 2) / 2}`;
      const c = binCounts.get(key) || 1;
      return 3 + Math.min(8, Math.ceil(Math.sqrt(c)));
    },
    [binCounts]
  );

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-zinc-100 px-3 py-2">
        <div>
          <h3 className="text-[13px] font-semibold text-zinc-900">Order coverage</h3>
          <p className="text-[11px] text-zinc-500">
            {loading
              ? 'Loading completed orders…'
              : error
              ? error
              : `${fmtNumber(total)} completed-order dropoffs · ${fmtNumber(uniqueStates)} ${uniqueStates === 1 ? 'state' : 'states'}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!loading && !error && uniqueStates > 0 ? (
            <span
              className="inline-flex items-center rounded-md bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-700"
              title="Unique US states represented on this map"
            >
              {fmtNumber(uniqueStates)} states
            </span>
          ) : null}
          <span className="inline-flex items-center gap-1 rounded-md bg-zinc-100 px-1.5 py-0.5 text-[10px] font-medium text-zinc-600">
            <FiMapPin className="h-3 w-3" /> Live
          </span>
        </div>
      </div>

      <div className="relative flex-1">
        {loading && points.length === 0 ? (
          <div className="absolute inset-0 flex items-center justify-center bg-zinc-50">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-zinc-200 border-t-indigo-500" />
          </div>
        ) : null}
        <MapContainer
          style={{ height: '100%', width: '100%', minHeight: 320 }}
          center={[39.8283, -98.5795]}
          zoom={4}
          minZoom={3}
          maxZoom={16}
          scrollWheelZoom
          zoomControl
        >
          <TileLayer
            url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
          />
          {bounds ? <FitAllControl bounds={bounds} /> : null}
          {points.map((p, idx) => {
            const stateCode = resolveMapPointState(p);
            const color = MAP_PALETTE[(stateCode ? stateCode.charCodeAt(0) + stateCode.charCodeAt(1) : idx) % MAP_PALETTE.length];
            return (
              <CircleMarker
                key={p.id || `${p.lat}-${p.lng}-${idx}`}
                center={[p.lat, p.lng]}
                radius={radiusFor(p)}
                pathOptions={{ color, weight: 0, fillOpacity: 0.45, fillColor: color }}
                eventHandlers={{
                  click: () => onPointClick?.(p),
                }}
              >
                <Tooltip direction="top" offset={[0, -4]} opacity={0.95}>
                  <span className="text-[11px] text-zinc-700">
                    {p.label}
                    {stateCode ? ` · ${stateCode}` : ''}
                  </span>
                </Tooltip>
              </CircleMarker>
            );
          })}
        </MapContainer>
      </div>
    </div>
  );
}
